import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_DOMAIN } from "config";
import CarCardTemplate from "components/CarCardTemplate";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { he } from "date-fns/locale";
import ContactDetails, { resolveContactDetails } from "components/ContactDetails";
import ContactUserIcon from "components/ContactUserIcon";
import OfferPaymentConfirmModal from "components/OfferPaymentConfirmModal";
import { Switch } from "components";
import { toast } from "react-toastify";
import { trackOnboardingEvent } from "../../onboarding/trackEvent";
import { useTour } from "../../onboarding";

registerLocale("he", he);

const FIRST_OFFER_SUBMITTED_KEY = "bmc_first_offer_submitted";

const toNumericPrice = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized = typeof value === "number" ? value : Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(normalized) ? normalized : 0;
};

function ContactPhoneIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.35 1.78.68 2.62a2 2 0 0 1-.45 2.11L8.1 9.64a16 16 0 0 0 6.26 6.26l1.19-1.19a2 2 0 0 1 2.11-.45c.84.33 1.72.56 2.62.68A2 2 0 0 1 22 16.92Z"
        stroke="#7CB342"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ApprovedContactDetailsInline = ({ data }) => {
  const { contactName, contactPhone, hasContactDetails } = resolveContactDetails(data);

  if (!hasContactDetails) return null;

  return (
    <div
      dir="rtl"
      style={{ backgroundColor: "white" }}
      className="rounded-[18px] border border-gray-200 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.14)] w-[360px] max-w-[92vw] p-5"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className="text-center font-heebo text-black text-[18px] font-medium">
        פרטי התקשרות
      </div>

      <div className="relative mt-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 shrink-0">
              <ContactUserIcon />
              <span className="font-heebo text-black text-[18px] font-medium">
                שם איש קשר:
              </span>
            </div>

            <span className="font-heebo text-[16px] text-black text-left break-words">
              {contactName || "-"}
            </span>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 shrink-0">
              <ContactPhoneIcon />
              <span className="font-heebo text-black text-[18px] font-medium">
                פלאפון:
              </span>
            </div>

            <span className="font-heebo text-[16px] text-black text-left" dir="ltr">
              {contactPhone || "-"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SendNewOfferForm({
  isRejectedOrApproved,
  car,
  currentUser,
  offer,
  onUpdateOffer,
  onClose,
  variant = "popup", // "popup" | "panel"
}) {
  const [updatedOffer, setUpdatedOffer] = useState(null);
  const [paymentConfirmOpen, setPaymentConfirmOpen] = useState(false);
  const [paymentConfirmMode, setPaymentConfirmMode] = useState("regular");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [canHoverDevice, setCanHoverDevice] = useState(false);
  const [showImmediatePurchaseTooltip, setShowImmediatePurchaseTooltip] = useState(false);
  const { runVehicleTour } = useTour();

  const MAX_PRICE_DIGITS = 7;

  const [offerForm, setOfferForm] = useState({
    price: offer?.offered_price ?? "",
    offerDate: offer?.creation_date ? new Date(offer.creation_date) : new Date(),
    pickupDate: offer?.collection_date ? new Date(offer.collection_date) : "",
    lastPaymentDate: offer?.last_payment_date ? new Date(offer.last_payment_date) : "",
    reserve: false,
  });

  const [errors, setErrors] = useState({ price: "" });

  const recommendedPrice = toNumericPrice(
    car?.recommended_price ?? car?.recommendedPrice ?? car?.asking_price ?? 0
  );

  const minAllowedOffer = Math.ceil(recommendedPrice * 0.85);

  const hasOfferAboveRecommendedPrice = useMemo(() => {
    const explicitFlag =
      car?.has_offer_above_recommended_price ?? car?.hasOfferAboveRecommendedPrice;

    if (explicitFlag !== undefined && explicitFlag !== null) {
      return Number(explicitFlag) === 1 || explicitFlag === true;
    }

    const currentRecommendedPrice = toNumericPrice(
      car?.recommended_price ?? car?.recommendedPrice ?? car?.asking_price
    );

    if (currentRecommendedPrice <= 0) return false;

    const carOffers = [
      ...(Array.isArray(car?.offers) ? car.offers : []),
      ...(Array.isArray(car?.all_offers) ? car.all_offers : []),
      ...(Array.isArray(car?.allOffers) ? car.allOffers : []),
    ];

    return carOffers.some((item) => {
      const offeredPrice = toNumericPrice(
        item?.offered_price ?? item?.offeredPrice ?? item?.price
      );
      return offeredPrice > currentRecommendedPrice;
    });
  }, [car]);

  const immediatePurchaseUnavailableTooltip =
    "לא ניתן לבצע רכישה מיידית, כיוון שקיימת הצעה גבוהה מהמחיר המומלץ.";
  const isImmediatePurchaseUnavailable = hasOfferAboveRecommendedPrice;

  const offerValidationMessage =
    "ניתן להגיש הצעה עד 15% מתחת למחיר המומלץ";
  const immediatePurchaseValidationMessage =
    "ברכישה מידית יש להזין סכום בגובה המחיר המומלץ";

  const FIELD_ROW_W = "clamp(220px, 16vw, 250px)";
  const FIELD_GAP_PX = 8;
  const FIELD_LABEL_W = "clamp(120px, 50%, 140px)";
  const FIELD_VALUE_W = `calc(${FIELD_ROW_W} - ${FIELD_LABEL_W} - ${FIELD_GAP_PX}px)`;

  const normalizeOfferLike = (src, fallback = {}) => {
    if (!src) return null;

    const o = src?.offer ? src.offer : src;

    const offered_price =
      o?.offered_price ?? o?.price ?? o?.offeredPrice ?? fallback?.offered_price;

    const creation_date =
      o?.creation_date ?? o?.offerDate ?? o?.creationDate ?? fallback?.creation_date;

    const collection_date =
      o?.collection_date ??
      o?.pickup_date ??
      o?.pickupDate ??
      fallback?.collection_date;

    const last_payment_date =
      o?.last_payment_date ?? o?.lastPaymentDate ?? fallback?.last_payment_date;

    const is_reserved = o?.is_reserved ?? o?.reserve ?? fallback?.is_reserved;

    return {
      id: o?.id ?? o?.offer_id ?? o?.offerId,
      offer_id: o?.offer_id ?? o?.id ?? o?.offerId,
      offered_price,
      creation_date,
      collection_date,
      last_payment_date,
      is_reserved,
      offer_status: o?.offer_status ?? o?.status ?? fallback?.offer_status,
      offer_position: o?.offer_position ?? o?.offerPosition ?? fallback?.offer_position,
      reservation_hold_id: o?.reservation_hold_id ?? fallback?.reservation_hold_id,
      payment_method_id: o?.payment_method_id ?? fallback?.payment_method_id,
    };
  };

  const hydrateFromOffer = (srcOffer) => {
    const norm = normalizeOfferLike(srcOffer);
    if (!norm) return false;

    setOfferForm({
      price: norm?.offered_price ?? "",
      offerDate: norm?.creation_date ? new Date(norm.creation_date) : new Date(),
      pickupDate: norm?.collection_date ? new Date(norm.collection_date) : "",
      lastPaymentDate: norm?.last_payment_date ? new Date(norm.last_payment_date) : "",
      reserve: false,
    });

    return true;
  };

  const OfferPriceField = ({ value, onChange, disabled = false, error = "" }) => {
    const pretty = value ? Number(value).toLocaleString("he-IL") : "";

    return (
      <div className="w-full flex flex-col items-center" style={{ marginTop: "-19px" }}>
        <div className="flex items-end justify-center gap-[7px]">
          <span className="!font-heebo text-[18px] font-normal text-black-900_03">
            ₪
          </span>

          {disabled ? (
            <span className="text-center text-[24px] font-bold text-red-600 leading-none">
              {pretty}
            </span>
          ) : (
            <input
              type="number"
              name="price"
              value={value}
              onChange={onChange}
              inputMode="numeric"
              className={`
                w-44 border-0 border-b
                ${error ? "border-red-500" : "border-blue_gray-400"}
                bg-transparent
                text-center
                focus:border-black focus:outline-none
              `}
              disabled={disabled}
            />
          )}
        </div>

        {!!error && <div className="mt-1 text-red-600 text-sm text-center">{error}</div>}
      </div>
    );
  };

  const CustomInput = React.forwardRef(({ value, onClick, label }, ref) => (
    <div className="flex items-center justify-center gap-2" style={{ width: FIELD_ROW_W }}>
      <input
        ref={ref}
        readOnly
        onClick={onClick}
        value={value ?? ""}
        placeholder="__/__/__"
        className="
          bg-transparent
          text-center
          cursor-pointer
          border-0
          border-b-0
          ring-0
          focus:ring-0
          focus:outline-none
          placeholder:text-gray-400
          m
        "
        style={{ width: FIELD_VALUE_W }}
      />

      <span
        className="text-[16px] text-black-900_03 lg:text-[13px] text-right whitespace-nowrap"
        style={{ width: FIELD_LABEL_W }}
      >
        {label}
      </span>
    </div>
  ));

  CustomInput.displayName = "CustomInput";

  useEffect(() => {
    const didHydrate = hydrateFromOffer(offer);

    if (!didHydrate) {
      hydrateFromOffer(car?.offer ?? car?.user_offer ?? car?.userOffer);
    }

    if (step !== 2) {
      setStep(1);
      setUpdatedOffer(null);
    }

    setErrors({ price: "" });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    offer?.offer_id,
    offer?.id,
    offer?.offered_price,
    offer?.creation_date,
    offer?.collection_date,
    offer?.last_payment_date,
    offer?.is_reserved,
    car?.id,
    car?.car_id,
    car?.offer?.offer_id,
    car?.offer?.id,
    car?.user_offer?.offer_id,
    car?.user_offer?.id,
    car?.userOffer?.offer_id,
    car?.userOffer?.id,
  ]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");

    const update = () => setCanHoverDevice(mq.matches);
    update();

    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }

    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  useEffect(() => {
    if (isImmediatePurchaseUnavailable && paymentConfirmMode === "immediatePurchase") {
      setPaymentConfirmMode("regular");
    }
  }, [isImmediatePurchaseUnavailable, paymentConfirmMode]);

  useEffect(() => {
    if (!isImmediatePurchaseUnavailable) {
      setShowImmediatePurchaseTooltip(false);
    }
  }, [isImmediatePurchaseUnavailable]);

  useEffect(() => {
    if (!showImmediatePurchaseTooltip) return undefined;

    const closeTooltip = () => setShowImmediatePurchaseTooltip(false);

    window.addEventListener("scroll", closeTooltip, true);
    window.addEventListener("touchmove", closeTooltip, { passive: true });

    return () => {
      window.removeEventListener("scroll", closeTooltip, true);
      window.removeEventListener("touchmove", closeTooltip);
    };
  }, [showImmediatePurchaseTooltip]);

  const popupWrapperStyle = useMemo(() => {
    const isPopup = variant === "popup";

    return {
      maxHeight: isPopup ? (canHoverDevice ? "none" : "calc(100vh - 110px)") : "none",
      overflowY: isPopup ? (canHoverDevice ? "visible" : "auto") : "visible",
      overflowX: "hidden",
      WebkitOverflowScrolling: "auto",
      paddingBottom: "20px",
      boxSizing: "border-box",
    };
  }, [variant, canHoverDevice]);

  const isEdit = !!offer?.id || !!offer?.offer_id || offer?.offer_status === 1;
  const isEditable = offer?.offer_status !== 2 && offer?.offer_status !== 3;

  const handlePriceChange = (e) => {
    let v = String(e.target.value ?? "").replace(/\D/g, "");

    if (v.length > MAX_PRICE_DIGITS) {
      v = v.slice(0, MAX_PRICE_DIGITS);
    }

    setOfferForm((prev) => ({ ...prev, price: v }));
    validatePrice(v, paymentConfirmMode);
  };

  const validatePrice = (price = offerForm.price, mode = paymentConfirmMode) => {
    const raw = String(price ?? "").trim();
    const val = Number(raw);

    if (!raw) {
      setErrors({ price: "יש להזין מחיר הצעה" });
      return false;
    }

    if (!Number.isFinite(val) || val <= 0) {
      setErrors({ price: "יש להזין מספר תקין" });
      return false;
    }

    if (recommendedPrice > 0) {
      const minPrice = mode === "immediatePurchase" ? recommendedPrice : minAllowedOffer;
      const message =
        mode === "immediatePurchase"
          ? immediatePurchaseValidationMessage
          : offerValidationMessage;

      if (val < minPrice) {
        setErrors({ price: message });
        return false;
      }
    }

    setErrors({ price: "" });
    return true;
  };

  const handleImmediatePurchaseChange = (checked) => {
    if (isImmediatePurchaseUnavailable) return;

    const mode = checked ? "immediatePurchase" : "regular";
    setPaymentConfirmMode(mode);

    if (checked || errors.price === immediatePurchaseValidationMessage) {
      validatePrice(offerForm.price, mode);
    }
  };

  const fmt = (d) => {
    return d ? new Date(d).toISOString().slice(0, 19).replace("T", " ") : null;
  };

  const openPaymentConfirm = async (mode = paymentConfirmMode) => {
    if (isSubmitting || paymentConfirmOpen) return;
    if (!validatePrice(offerForm.price, mode)) return;

    const resolvedUserId =
      Number(currentUser?.id ?? offer?.user_id ?? car?.user_id ?? offer?.userId ?? car?.userId) ||
      null;

    if (!resolvedUserId) {
      alert("שגיאה: לא נמצא משתמש מחובר. נסה לרענן את העמוד.");
      return;
    }

    setPaymentConfirmMode(mode);
    setPaymentConfirmOpen(true);
  };

  const submitOffer = async (paymentContext) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const carId = Number(car?.car_id ?? car?.id);
    const currentOfferId = Number(offer?.offer_id ?? offer?.id);
    const resolvedUserId =
      Number(currentUser?.id ?? offer?.user_id ?? car?.user_id ?? offer?.userId ?? car?.userId) ||
      null;

    const payload = {
      car_id: carId,
      user_id: resolvedUserId,
      offered_price: Number(offerForm.price || recommendedPrice || 0),
      collection_date: fmt(offerForm.pickupDate),
      last_payment_date: fmt(offerForm.lastPaymentDate),
      is_reserved: paymentContext?.immediate_purchase ? 0 : offerForm.reserve ? 1 : 0,
      creation_date: offerForm.offerDate.toISOString().slice(0, 19).replace("T", " "),
      ...(paymentContext || {}),
    };

    try {
      let saveResponse = null;
      if (currentOfferId) {
        saveResponse = await axios.post(`${API_DOMAIN}/offer/updateOffer/${currentOfferId}`, payload);
      } else {
        saveResponse = await axios.post(`${API_DOMAIN}/offer/addOffer`, payload);
      }
      const saved = saveResponse?.data || {};

      const resp = await axios.get(`${API_DOMAIN}/offer/getUserOffer`, {
        params: { car_id: carId, user_id: resolvedUserId },
      });

      const raw = resp?.data;
      const fresh = Array.isArray(raw) ? raw[0] : raw?.offer ? raw.offer : raw;

      const upd = {
        ...(fresh || {}),
        id: Number(fresh?.id ?? fresh?.offer_id ?? saved?.offer_id ?? currentOfferId) || undefined,
        offer_id: Number(fresh?.offer_id ?? fresh?.id ?? saved?.offer_id ?? currentOfferId) || undefined,
        car_id: carId,
        offered_price: Number(fresh?.offered_price ?? offerForm.price),
        collection_date: fresh?.collection_date ?? fmt(offerForm.pickupDate),
        last_payment_date: fresh?.last_payment_date ?? fmt(offerForm.lastPaymentDate),
        is_reserved: Number(fresh?.is_reserved ?? (paymentContext?.immediate_purchase ? 0 : offerForm.reserve ? 1 : 0)),
        offer_status: Number(
          saved?.offer_status ??
          fresh?.offer_status ??
          fresh?.status ??
          (paymentContext?.immediate_purchase ? 2 : offer?.offer_status ?? 1)
        ),
        immediate_purchase: Number(saved?.immediate_purchase ?? fresh?.immediate_purchase ?? paymentContext?.immediate_purchase ?? 0),
        immediate_purchase_transaction_id: fresh?.immediate_purchase_transaction_id ?? paymentContext?.immediate_purchase_transaction_id,
        offer_position: fresh?.offer_position ?? fresh?.offerPosition,
        reservation_hold_id: fresh?.reservation_hold_id ?? paymentContext?.reservation_hold_id ?? offer?.reservation_hold_id,
        payment_method_id: fresh?.payment_method_id ?? paymentContext?.payment_method_id ?? offer?.payment_method_id,
      };

      setUpdatedOffer(upd);
      onUpdateOffer?.(upd);
      setPaymentConfirmOpen(false);
      if (paymentContext?.immediate_purchase) {
        if (variant === "popup") {
          onClose?.();
        } else {
          setStep(1);
        }
        return;
      }
      setStep(2);

      if (!currentOfferId && localStorage.getItem(FIRST_OFFER_SUBMITTED_KEY) !== "true") {
        localStorage.setItem(FIRST_OFFER_SUBMITTED_KEY, "true");
        toast.success('🎉 ההצעה נשלחה! תוכלו לעקוב אחריה בעמוד "הצעות שלי"');
        trackOnboardingEvent("first_offer_submitted", {
          vehicleId: carId,
          offerAmount: Number(offerForm.price),
          fromTour: Boolean(runVehicleTour),
        });
      }
    } catch (err) {
      const serverMessage = err?.response?.data?.error;
      if (serverMessage) {
        setErrors({ price: serverMessage });
        return;
      }
      alert("אירעה שגיאה בשמירת ההצעה");
    } finally {
      setIsSubmitting(false);
    }
  };

  const myOfferRankText = useMemo(() => {
    const posRaw =
      updatedOffer?.offer_position ??
      offer?.offer_position ??
      offer?.offerPosition ??
      car?.offer?.offer_position ??
      car?.user_offer?.offer_position ??
      car?.userOffer?.offer_position;

    const pos = Number(posRaw);

    if (!Number.isFinite(pos) || pos <= 0) return "";

    return String(pos);
  }, [updatedOffer?.offer_position, offer?.offer_position, offer?.offerPosition, car]);

  const effectiveOfferStatus = Number(updatedOffer?.offer_status ?? offer?.offer_status ?? 0);
  const isApproved = effectiveOfferStatus === 2;

  const contactData = useMemo(() => {
    const src = { ...(car || {}), ...(offer || {}), ...(updatedOffer || {}) };

    return {
      ...src,

      supplier_name:
        src.supplier_name ?? src.provider_name ?? src.business_name ?? src.name ?? null,

      contact_name:
        src.contact_name ?? src.provider_contact_name ?? src.contactName ?? null,

      contact_phone:
        src.contact_phone ??
        src.provider_phone_number ??
        src.phone_number ??
        src.contactPhone ??
        null,

      contact_location:
        src.contact_location ?? src.full_address ?? src.contactLocation ?? null,
    };
  }, [car, offer, updatedOffer]);

  return (
    <>
      {variant === "popup" && (
        <button className="x-close" onClick={onClose}>
          ✕
        </button>
      )}

      <OfferPaymentConfirmModal
        open={paymentConfirmOpen}
        userId={Number(currentUser?.id ?? offer?.user_id ?? car?.user_id ?? offer?.userId ?? car?.userId)}
        car={car}
        offerAmount={offerForm.price}
        recommendedPrice={recommendedPrice}
        wantsReservation={paymentConfirmMode === "regular" && offerForm.reserve}
        mode={paymentConfirmMode}
        onClose={() => setPaymentConfirmOpen(false)}
        onSubmitConfirmed={submitOffer}
        offerId={updatedOffer?.offer_id ?? offer?.offer_id ?? offer?.id}
        existingReservationHoldId={updatedOffer?.reservation_hold_id ?? offer?.reservation_hold_id}
        existingPaymentMethodId={updatedOffer?.payment_method_id ?? offer?.payment_method_id}
      />

      {step === 1 && (
        <div className="w-full" style={popupWrapperStyle}>
          <h2
            className="add-car-title mt-3 left-side snop-title text-center"
            style={{ color: "black", marginBottom: "1.5rem" }}
          >
            {effectiveOfferStatus !== 2 && effectiveOfferStatus !== 3 && (
              <>{isEdit ? "עדכן את ההצעה שלך" : "הגש הצעה חדשה"}</>
            )}
          </h2>

          <div
            className="flex mt-3 justify-center w-full min-w-0"
            style={{
              width: "100%",
              maxWidth: variant === "panel" ? "100%" : FIELD_ROW_W,
              marginInline: "auto",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <CarCardTemplate
                key={car?.id}
                offer_status={offer?.offer_status ?? 0}
                isRejectedOrApproved={isRejectedOrApproved}
                {...car}
                className="w-full self-center"
                offered_price={offerForm.price}
              />
            </div>
          </div>

          <div
            className={`w-full flex justify-center min-w-0 ${
              canHoverDevice ? "overflow-visible" : "overflow-x-hidden"
            }`}
          >
            <div
              className="snop-form mt-3.5 w-full"
              style={{
                width: FIELD_ROW_W,
                minWidth: FIELD_ROW_W,
                maxWidth: FIELD_ROW_W,
                margin: "14px auto 0",
                float: "none",
                position: "static",
                left: "auto",
                right: "auto",
                paddingInline: 0,
                flexShrink: 0,
              }}
            >
              <div
                style={{ marginTop: "-19px" }}
                className="w-full flex flex-col items-center gap-5"
              >
                <div className="min-price-display flex justify-center w-full" data-testid="min-price-display">
                  <div
                    className="flex items-center justify-center gap-2 mt-5"
                    style={{ width: FIELD_ROW_W }}
                  >
                    <div className="flex justify-center" style={{ width: FIELD_VALUE_W }}>
                      <span className="text-red-600 font-semibold text-[18px]">
                        ₪{Number(recommendedPrice ?? 0).toLocaleString("he-IL")}
                      </span>
                    </div>

                    <div className="text-right" style={{ width: FIELD_LABEL_W }}>
                      <span className="text-[16px] text-black-900_03 lg:text-[13px]">
                        מחיר מומלץ
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center w-full">
                  <div
                    className="flex items-center justify-center gap-2"
                    style={{ width: FIELD_ROW_W }}
                  >
                    <div style={{ width: FIELD_VALUE_W }} />

                    <div className="text-right" style={{ width: FIELD_LABEL_W }}>
                      <span className="text-[16px] text-black-900_03 lg:text-[13px] font-medium">
                        תן הצעת מחיר
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center">
                  <div className="flex items-end justify-center gap-[7px]">
                    <span className="!font-heebo text-[18px] font-normal text-black-900_03 leading-none">
                      ₪
                    </span>

                    <input
                      type="number"
                      name="price"
                      value={offerForm.price}
                      onChange={handlePriceChange}
                      inputMode="numeric"
                      className={`
                        offer-amount-input w-44 border-0 border-b
                        ${errors.price ? "border-red-500" : "border-blue_gray-400"}
                        bg-transparent
                        text-center
                        text-[24px] font-bold text-red-600 leading-none
                        focus:border-black focus:outline-none
                      `}
                      data-testid="offer-amount-input"
                      disabled={!isEditable}
                    />
                  </div>

                  {!!errors.price && (
                    <div className="mt-1 text-red-600 text-sm text-center">
                      {errors.price}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 items-center justify-center wrap-offer-date">
                  <DatePicker
                    style={{ zIndex: "9999" }}
                    popperClassName="my-datepicker-popper"
                    locale="he"
                    selected={offerForm.offerDate}
                    dateFormat="dd/MM/yyyy"
                    calendarStartDay={0}
                    disabled
                    customInput={<CustomInput label=":תאריך ההצעה" />}
                    popperContainer={({ children }) => (
                      <div style={{ zIndex: 99999 }}>{children}</div>
                    )}
                    popperPlacement="bottom-start"
                  />

                  <DatePicker
                    locale="he"
                    style={{ zIndex: "99" }}
                    withPortal
                    popperClassName="my-datepicker-popper"
                    selected={offerForm.pickupDate}
                    onChange={(d) => setOfferForm((p) => ({ ...p, pickupDate: d }))}
                    dateFormat="dd/MM/yyyy"
                    calendarStartDay={0}
                    customInput={<CustomInput label=":תאריך איסוף" />}
                    popperContainer={({ children }) => (
                      <div style={{ zIndex: 99999 }}>{children}</div>
                    )}
                    popperPlacement="bottom-start"
                    disabled={!isEditable}
                    minDate={new Date()}
                  />

                  <DatePicker
                    locale="he"
                    withPortal
                    selected={offerForm.lastPaymentDate}
                    popperClassName="my-datepicker-popper"
                    onChange={(d) =>
                      setOfferForm((p) => ({ ...p, lastPaymentDate: d }))
                    }
                    dateFormat="dd/MM/yyyy"
                    calendarStartDay={0}
                    customInput={<CustomInput label=":תאריך סיום תשלום" />}
                    popperContainer={({ children }) => (
                      <div style={{ zIndex: 1000 }}>{children}</div>
                    )}
                    popperPlacement="bottom-start"
                    disabled={!isEditable}
                    minDate={new Date()}
                  />
                </div>

                <div
                  className="reservation-toggle relative w-full flex flex-col items-center justify-center gap-2"
                  data-testid="immediate-purchase-action"
                  dir="rtl"
                  onPointerEnter={(e) => {
                    if (e.pointerType === "mouse" && isImmediatePurchaseUnavailable) {
                      setShowImmediatePurchaseTooltip(true);
                    }
                  }}
                  onPointerLeave={(e) => {
                    if (e.pointerType === "mouse") {
                      setShowImmediatePurchaseTooltip(false);
                    }
                  }}
                  style={{
                    zIndex: showImmediatePurchaseTooltip ? 100000 : "auto",
                  }}
                >
                  {isImmediatePurchaseUnavailable && showImmediatePurchaseTooltip && (
                    <div
                      role="tooltip"
                      className={`rounded-[12px] border border-gray-200 bg-white p-3 text-right font-heebo text-[15px] font-medium leading-[1.5] text-black shadow-[0_14px_32px_rgba(0,0,0,0.16)] ${
                        canHoverDevice
                          ? "absolute bottom-[calc(100%+12px)] right-1/2 translate-x-1/2"
                          : "relative"
                      }`}
                      style={{
                        backgroundColor: "#fff",
                        maxWidth: "100%",
                        width: "min(250px, 100%)",
                        zIndex: 100001,
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FDECEC] text-[16px] font-bold leading-none text-[#CE0000]">
                          !
                        </span>
                        <span>{immediatePurchaseUnavailableTooltip}</span>
                      </div>
                      <span className="absolute -bottom-1 right-1/2 h-2 w-2 translate-x-1/2 rotate-45 border-b border-r border-gray-200 bg-white" />
                    </div>
                  )}
                  <div
                    className="relative flex items-center justify-center gap-3"
                    style={{
                      cursor: isImmediatePurchaseUnavailable ? "not-allowed" : undefined,
                    }}
                  >
                    <span
                      className={`text-[16px] text-black-900_03 lg:text-[13px] ${
                        isImmediatePurchaseUnavailable ? "opacity-60" : ""
                      }`}
                    >
                      רכישה מידית
                    </span>
                    <Switch
                      checked={paymentConfirmMode === "immediatePurchase"}
                      onCheckedChange={handleImmediatePurchaseChange}
                      disabled={!isEditable || isSubmitting || paymentConfirmOpen || isImmediatePurchaseUnavailable}
                      className={`immediate-purchase-switch ${
                        isImmediatePurchaseUnavailable ? "opacity-60" : ""
                      }`}
                      size="sm"
                    />
                    {isImmediatePurchaseUnavailable && (
                      <button
                        type="button"
                        aria-label={immediatePurchaseUnavailableTooltip}
                        className="absolute inset-0 z-10 cursor-not-allowed bg-transparent"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowImmediatePurchaseTooltip((prev) => !prev);
                        }}
                      />
                    )}
                  </div>
                </div>

                {isApproved && (
                  <div className="w-full flex justify-center relative mt-3" dir="rtl">
                    {canHoverDevice ? (
                      <ApprovedContactDetailsInline data={{ ...contactData }} />
                    ) : (
                      <ContactDetails
                        data={{ ...contactData }}
                        buttonClassName="min-w-[140px] rounded-[14px] px-[16px] py-[10px] font-assistant font-medium border border-gray-200 bg-white shadow-sm flex items-center justify-center gap-2"
                        tooltipWrapperClassName="absolute bottom-[calc(100%+10px)] left-1/2 -translate-x-1/2 z-[2000]"
                        showArrow
                      />
                    )}
                  </div>
                )}

                {effectiveOfferStatus !== 2 && effectiveOfferStatus !== 3 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      marginTop: "1rem",
                    }}
                  >
                    <button
                      className="submit-offer-btn z-10 min-w-[185px] gap-2 py-2 rounded-[50px] px-3 font-semibold text-white snop-submitBtn"
                      data-testid="submit-offer-btn"
                      style={{
                        background: "black",
                        color: "white",
                        width: "auto",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => openPaymentConfirm()}
                      disabled={isSubmitting || paymentConfirmOpen}
                    >
                      <span className="flex items-center justify-center gap-2 whitespace-nowrap">
                        {paymentConfirmMode === "immediatePurchase"
                          ? "לרכישה"
                          : isEdit ? "עדכן הצעת מחיר" : "שלח הצעת מחיר"}
                        <img
                          src="/images/offer-white.svg"
                          alt="offer"
                          className="h-4 w-4"
                        />
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="w-full" style={popupWrapperStyle}>
          <div className="w-full flex justify-center mt-2 mb-2">
            <h2
              className="add-car-title mt-3 left-side snop-title text-center"
              style={{ color: "black", marginBottom: "1.5rem" }}
            >
              {effectiveOfferStatus !== 2 && effectiveOfferStatus !== 3 && (
                <span>
                  {isEdit
                    ? "ההצעה שלך עודכנה בהצלחה"
                    : "ההצעה שלך נשלחה בהצלחה"}
                </span>
              )}
            </h2>
          </div>

          <div className="flex justify-center w-full">
            <CarCardTemplate
              key={car?.id}
              {...car}
              offer_status={updatedOffer?.offer_status ?? 1}
              offered_price={offerForm.price}
              className="w-full max-w-[300px] sm:w-[100%] self-center"
            />
          </div>

          <div
            className={`w-full flex justify-center min-w-0 ${
              canHoverDevice ? "overflow-visible" : "overflow-x-hidden"
            }`}
          >
            <div
              className="snop-form mt-3.5 w-full"
              style={{
                width: FIELD_ROW_W,
                minWidth: FIELD_ROW_W,
                maxWidth: FIELD_ROW_W,
                margin: "14px auto 0",
                float: "none",
                position: "static",
                left: "auto",
                right: "auto",
                paddingInline: 0,
                flexShrink: 0,
              }}
            >
              <div className="w-full flex justify-center">
                <h2
                  className="add-car-title mt-3 left-side snop-title text-center"
                  style={{ color: "black", marginBottom: "0.75rem" }}
                >
                  {isEdit
                    ? "ההצעה שלך עודכנה בהצלחה"
                    : "ההצעה שלך נשלחה בהצלחה"}
                </h2>
              </div>

              <div className="w-full flex flex-col items-center gap-5">
                <div className="flex justify-center w-full">
                  <div
                    className="flex items-center justify-center gap-2"
                    style={{ width: FIELD_ROW_W }}
                  >
                    <div className="text-center" style={{ width: FIELD_LABEL_W }}>
                      <span className="text-[16px] text-black-900_03 lg:text-[13px]">
                        הצעת מחיר שלי
                      </span>
                    </div>
                  </div>
                </div>

                <OfferPriceField
                  value={offerForm.price}
                  onChange={handlePriceChange}
                  disabled={true}
                  error={errors.price}
                />

                <div className="text-center" style={{ width: FIELD_LABEL_W }}>
                  <div className="flex flex-row-reverse items-center justify-center gap-2">
                    <span className="text-[16px] text-black-900_03 lg:text-[13px]">
                      מיקומך: {myOfferRankText}
                    </span>

                    <img src="/images/yourPlace.svg" alt="offer" />
                  </div>
                </div>

                <div className="flex flex-col gap-2 items-center justify-center">
                  <DatePicker
                    style={{ zIndex: "9999" }}
                    popperClassName="my-datepicker-popper"
                    locale="he"
                    selected={offerForm.offerDate}
                    dateFormat="dd/MM/yyyy"
                    calendarStartDay={0}
                    disabled
                    customInput={<CustomInput label=":תאריך ההצעה" />}
                    popperContainer={({ children }) => (
                      <div style={{ zIndex: 99999 }}>{children}</div>
                    )}
                    popperPlacement="bottom-start"
                  />

                  <DatePicker
                    locale="he"
                    style={{ zIndex: "99" }}
                    withPortal
                    popperClassName="my-datepicker-popper"
                    selected={offerForm.pickupDate}
                    onChange={() => {}}
                    dateFormat="dd/MM/yyyy"
                    calendarStartDay={0}
                    disabled
                    customInput={<CustomInput label=":תאריך איסוף" />}
                    popperContainer={({ children }) => (
                      <div style={{ zIndex: 99999 }}>{children}</div>
                    )}
                    popperPlacement="bottom-start"
                    minDate={new Date()}
                  />

                  <DatePicker
                    locale="he"
                    withPortal
                    selected={offerForm.lastPaymentDate}
                    popperClassName="my-datepicker-popper"
                    onChange={() => {}}
                    dateFormat="dd/MM/yyyy"
                    calendarStartDay={0}
                    disabled
                    customInput={<CustomInput label=":תאריך סיום תשלום" />}
                    popperContainer={({ children }) => (
                      <div style={{ zIndex: 1000 }}>{children}</div>
                    )}
                    popperPlacement="bottom-start"
                    minDate={new Date()}
                  />
                </div>

                {isApproved && (
                  <div className="w-full flex justify-center relative mt-3" dir="rtl">
                    {canHoverDevice ? (
                      <ApprovedContactDetailsInline data={{ ...contactData }} />
                    ) : (
                      <ContactDetails
                        data={{ ...contactData }}
                        buttonClassName="min-w-[140px] rounded-[14px] px-[16px] py-[10px] font-assistant font-medium border border-gray-200 bg-white shadow-sm flex items-center justify-center gap-2"
                        tooltipWrapperClassName="absolute bottom-[calc(100%+10px)] left-1/2 -translate-x-1/2 z-[2000]"
                        showArrow
                      />
                    )}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    marginTop: "1rem",
                  }}
                >
                  <button
                    className="z-10 min-w-[185px] gap-2 py-2 rounded-[50px] px-3 font-semibold text-white snop-submitBtn"
                    style={{
                      background: "black",
                      color: "white",
                      width: "auto",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => setStep(1)}
                  >
                    <span className="flex items-center justify-center gap-2 whitespace-nowrap">
                      שנה הצעה
                      <img
                        src="/images/offer-white.svg"
                        alt="offer"
                        className="h-4 w-4"
                      />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
